import React, { useEffect, useState } from "react";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { TimePicker } from "antd";
import "antd/dist/reset.css";
import { useSelector } from "react-redux";
const dayjs = require("dayjs");

const RescheduleAppointment = ({
  setShowRescheduleModal,
  appointmentData,
  fetchAppointmentData,
}) => {
  const user = useSelector((state) => state?.user);

  const [date, setDate] = useState(null);
  const [dbtime, setDbTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [time, setTime] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [rescheduleType, setRescheduleType] = useState("single");
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [seriesInfo, setSeriesInfo] = useState(null);

  // Pre-fill form data when rescheduling
  useEffect(() => {
    if (appointmentData) {
      setDate(new Date(appointmentData.appointmenttime));
      setDbTime(convertUTCToLocal(appointmentData.appointmenttime));
      setDuration(appointmentData.sessionLength);
      setTime(dayjs(new Date(appointmentData.appointmenttime)));
      
      // Check if this is a recurring appointment
      if (appointmentData.isRecurring) {
        fetchSeriesInfo(appointmentData._id);
      }
    }
  }, [appointmentData]);

  // Force z-index for picker popups when modal is open
  useEffect(() => {
    const forceZIndex = () => {
      const pickerPopups = document.querySelectorAll('.rs-picker-popup');
      pickerPopups.forEach(popup => {
        popup.style.zIndex = '10001';
      });
    };

    forceZIndex();

    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  const fetchSeriesInfo = async (appointmentId) => {
    try {
      const authToken = user.user?.token;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/series/${appointmentId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        setSeriesInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching series info:", error);
    }
  };

  const convertUTCToLocal = (utcTime) => {
    const date = new Date(utcTime);
    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return formattedTime;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    // Check if the session has ended
    if (hasSessionEnded()) {
      toast.error(
        "This session has ended. You cannot reschedule it.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      setIsLoading(false);
      return;
    }

    if (!date || !time) {
      toast.error("Please select a date and time to reschedule the session", {
        position: "top-right",
        autoClose: 5000,
      });
      setIsLoading(false);
      return;
    }

    const authToken = user.user?.token;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/reschedule`,
        {
          appointmentId: appointmentData._id,
          newDate: date.toISOString().split('T')[0],
          newTime: dbtime,
          duration,
          timeZone: clientTimeZone,
          rescheduleType,
          reason: reason || "Rescheduled by provider"
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (request.data.success) {
        fetchAppointmentData();
        toast.success(request.data.message);
        setTimeout(() => {
          setShowRescheduleModal(false);
        }, 3000);
        return;
      } else {
        toast.error("Could not reschedule the appointment.");
      }
    } catch (error) {
      if (error.message === "Network Error") {
        toast.error(
          "We're having trouble processing your request. Please check your internet connection and try again.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        toast.error(
          error.response?.data?.message || "Error rescheduling appointment"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setShowRescheduleModal(false);
  };

  // Check if the appointment is currently in progress
  const isAppointmentInProgress = () => {
    const appointmentTime = new Date(appointmentData.appointmenttime);
    const currentTime = new Date();
    const endTime = new Date(appointmentTime);
    endTime.setMinutes(endTime.getMinutes() + appointmentData.sessionLength);

    return currentTime >= appointmentTime && currentTime <= endTime;
  };

  // Check if the session has ended (end time has passed)
  const hasSessionEnded = () => {
    const appointmentTime = new Date(appointmentData.appointmenttime);
    const currentTime = new Date();
    const endTime = new Date(appointmentTime);
    endTime.setMinutes(endTime.getMinutes() + appointmentData.sessionLength);

    return currentTime > endTime;
  };

  function extractTime(timeString) {
    const timeRegex = /(\d{2}:\d{2})/;
    const match = timeRegex.exec(timeString);

    if (match) {
      return match[1];
    } else {
      return null;
    }
  }

  // Return null if the form is closed
  if (!showForm) return null;

  return (
    <div style={containerStyle}>
      <form onSubmit={handleSubmit}>
        <div style={cardStyle}>
          {/* Title and Close Button */}
          <div style={headerStyle}>
            <h3 style={sectionTitleStyle}>Reschedule Appointment</h3>
            <button style={closeButtonStyle} onClick={handleClose}>
              X
            </button>
          </div>

          {/* Recurring Appointment Options */}
          {appointmentData?.isRecurring && (
            <div style={formGroupStyle}>
              <label style={labelStyle}>Reschedule Options:</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    value="single"
                    checked={rescheduleType === "single"}
                    onChange={(e) => setRescheduleType(e.target.value)}
                    style={radioStyle}
                  />
                  Reschedule this appointment only
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    value="series"
                    checked={rescheduleType === "series"}
                    onChange={(e) => setRescheduleType(e.target.value)}
                    style={radioStyle}
                  />
                  Reschedule this and all future appointments
                </label>
              </div>
              {seriesInfo && (
                <div style={seriesInfoStyle}>
                  <p>This is part of a recurring series with {seriesInfo.seriesAppointments?.length || 0} appointments.</p>
                </div>
              )}
            </div>
          )}

          {/* Date, Time, Duration */}
          <div style={formRowStyle}>
            <div style={{ flex: 1, marginRight: "0px" }}>
              <DatePicker
                value={date}
                onChange={setDate}
                format="MM/dd/yyyy"
                className="datepickerBorder"
                style={{
                  ...inputStyle,
                  width: "100%",
                  borderRadius: "8px 0px 0px 8px",
                }}
              />
            </div>
            <div style={{ flex: 1, marginRight: "10px", width: "50%" }}>
              <TimePicker
                minuteStep={15}
                hourStep={1}
                format="h:mm a"
                use12Hours
                value={time}
                onChange={(value) => {
                  if (value) {
                    setTime(value);
                    setDbTime(extractTime(value?.$d));
                  } else {
                    setTime(null);
                  }
                }}
                style={{
                  ...inputStyle,
                  borderRadius: "0px 8px 8px 0px",
                  borderLeft: "1px solid #ccc",
                }}
              />
            </div>
            <div style={{ flex: 1, paddingRight: "0px" }}>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                style={{ ...inputStyle, width: "50%" }}
              >
                <option value="30">30</option>
                <option value="60">60</option>
              </select>
              <span style={{ marginLeft: "8px" }}>mins</span>
            </div>
          </div>

          {/* Reason for Rescheduling */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Reason for Rescheduling (Optional):</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for rescheduling..."
              style={{
                ...inputStyle,
                minHeight: "80px",
                resize: "vertical",
                fontFamily: "inherit"
              }}
            />
          </div>

          {/* Submit Button */}
          <div style={buttonContainerStyle}>
            <button 
              type="submit" 
              style={buttonStyle}
              disabled={isLoading}
            >
              {isLoading ? "Rescheduling..." : "Reschedule Appointment"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Styles
const containerStyle = {
  maxWidth: "450px",
  margin: "50px auto",
  fontFamily: "Poppins, sans-serif",
};

const cardStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  width: "120%",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  color: "#333",
  textAlign: "left",
  fontWeight: "700",
  fontSize: "16px",
  fontFamily: "Poppins",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const closeButtonStyle = {
  borderRadius: "50rem",
  border: "none",
  width: "25px",
  height: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "14px",
  color: "#808080",
  cursor: "pointer",
};

const formGroupStyle = {
  marginBottom: "20px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: "600",
  color: "#333",
  fontSize: "14px",
};

const radioGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const radioLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  fontSize: "14px",
  color: "#555",
};

const radioStyle = {
  margin: 0,
  cursor: "pointer",
};

const seriesInfoStyle = {
  marginTop: "8px",
  padding: "8px 12px",
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  fontSize: "12px",
  color: "#666",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "#F4F4F6",
  height: "45px",
  boxSizing: "border-box",
  color: "#808080",
};

const formRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "20px",
};

const buttonContainerStyle = {
  textAlign: "center",
  marginTop: "20px",
};

const buttonStyle = {
  backgroundColor: "#27B99A",
  color: "white",
  padding: "12px 24px",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  marginTop: "20px",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "uppercase",
  width: "100%",
  transition: "all 0.2s",
  opacity: (props) => props.disabled ? 0.6 : 1,
  cursor: (props) => props.disabled ? "not-allowed" : "pointer",
};

export default RescheduleAppointment; 